// TXN_* — per-transaction adjustments (insurance, rent, legal, meals, vehicle,
// wages, capitalization candidates).

// ─── Custom resolvers ────────────────────────────────────────────────────────

// TXN_CAP_01 per-item decision: counts the txn_cap01_item_<n> response keys
// against the peer step's findings list to decide if all items are resolved.
function resolveTxnCap01PerItem(resolved, ctx) {
	let itemKeys = Object.keys(ctx.userResponses).filter(function (k) { return k.startsWith("txn_cap01_item_"); });
	if (itemKeys.length === 0) return resolved;
	let showStep = ctx.steps.find(function (s) { return s.id === "txn_cap01_show_items"; });
	let totalItems = showStep && showStep.data && showStep.data.findings ? showStep.data.findings.length : itemKeys.length;
	if (itemKeys.length >= totalItems) {
		return { ...resolved, status: "completed", detail: "All " + totalItems + " item(s) resolved", prompt: null };
	}
	return { ...resolved, status: "waiting_for_human", detail: itemKeys.length + " of " + totalItems + " items resolved — " + (totalItems - itemKeys.length) + " pending" };
}

// ─── Registry ────────────────────────────────────────────────────────────────

export const transactionCascades = {
	// TXN_INS_01 — insurance prepaid/accrual
	"txn_ins01_determine_action": {
		type: "labelResponse",
		labels: {
			prepaid_needed: "Coverage extends beyond year end — record prepaid",
			accrual_needed: "Coverage gap at year end — record accrual",
			annual_policy: "Annual policy — no adjustment needed",
			coverage_confirmed: "Coverage is complete — no adjustment needed",
		},
	},
	"txn_ins01_propose_aje": {
		type: "branch", of: "txn_ins01_determine_action",
		when: {
			"annual_policy,coverage_confirmed": { type: "acknowledge", detail: "No adjustment needed — acknowledged" },
		},
	},

	// TXN_RENT_01 — rent prepaid/accrual
	"txn_rent01_determine_action": {
		type: "labelResponse",
		labels: {
			prepaid_needed: "Coverage extends beyond year end — record prepaid",
			accrual_needed: "Coverage gap at year end — record accrual",
			annual_lease: "Annual lease payment — no adjustment needed",
			moved_premises: "Moved premises mid-year — no adjustment needed",
			coverage_confirmed: "Coverage is complete — no adjustment needed",
		},
	},
	"txn_rent01_propose_aje": {
		type: "branch", of: "txn_rent01_determine_action",
		when: {
			"annual_lease,moved_premises,coverage_confirmed": { type: "acknowledge", detail: "No adjustment needed — acknowledged" },
		},
	},

	// TXN_LEG_01 — legal fees personal vs business
	"txn_leg01_confirm": {
		type: "labelResponse",
		labels: {
			all_business: "All legal fees are business-related — confirmed",
			mixed: "Some fees are personal shareholder legal — needs reclassification",
			unknown: "Escalated to partner for review",
		},
	},

	// TXN_MEAL_01 — meals 50% limitation
	"txn_meal01_confirm": {
		type: "labelResponse",
		labels: {
			acknowledged: "50% limitation noted for tax return",
			not_applicable: "Not applicable — all meals qualify for full deduction",
		},
	},

	// TXN_VEH_01 — vehicle personal-use %
	"txn_veh01_confirm": {
		type: "labelResponse",
		labels: {
			logbook_available: "Logbook available — personal-use % confirmed",
			estimate_used: "Reasonable estimate used — noted for tax return",
			fully_business: "100% business use — no adjustment",
		},
	},

	// TXN_WAGE_01 — payroll T4 confirmation
	"txn_wage01_confirm": {
		type: "labelResponse",
		labels: {
			confirmed: "T4 summary and payroll remittances confirmed",
			owner_only: "Owner-managed — no employees, no T4s needed",
			external_payroll: "External payroll provider handles T4s and remittances",
		},
	},

	// TXN_CAP_01 — per-item capitalization decisions
	"txn_cap01_per_item_decision": { type: "custom", resolve: resolveTxnCap01PerItem },
};
